import * as OwnerActions from '@/redux/modules/owner/type';
import { ILocation } from '@/services/types/core/locations/id';
import { IReview } from '@/services/types/core/reviews/id';
import { IListingData, IMeta } from '@/services/types/search/rentals/id';
import { IUserResponseAttributes } from '@/services/types/search/users/id';

type TOwnerLocationsFallback = {
  city: string;
  state: string;
};

interface IOwnerState {
  owner: {
    id: string | null;
    data: Partial<IUserResponseAttributes> | null;
    isFetching: boolean;
    error?: boolean;
  };
  rentals: {
    data: Array<IListingData>;
    meta: IMeta | null;
    isFetching: boolean;
    error?: boolean;
  };
  reviews: {
    data: Array<IReview>;
    totalReviews: number;
    isFetching: boolean;
    error?: boolean;
  };
  locations: {
    data: ILocation[];
    fallback?: TOwnerLocationsFallback;
    isFetching: boolean;
    error?: boolean;
  };
}

export const initialState: IOwnerState = {
  owner: {
    id: null,
    data: null,
    isFetching: false,
    error: false,
  },
  rentals: {
    data: [],
    meta: null,
    isFetching: false,
    error: false,
  },
  reviews: {
    data: [],
    totalReviews: 0,
    isFetching: false,
    error: false,
  },
  locations: {
    data: [],
    fallback: {
      city: '',
      state: '',
    },
    isFetching: false,
    error: false,
  },
};

export default function reducer(
  state: IOwnerState = initialState,
  action:
    | OwnerActions.IOwnerAction
    | OwnerActions.IOwnerRentalsAction
    | OwnerActions.IOwnerReviewsAction
    | OwnerActions.IOwnerLocationsAction,
): IOwnerState {
  switch (action.type) {
    case OwnerActions.FETCH_OWNER_PENDING:
      return {
        ...state,
        owner: {
          ...state.owner,
          isFetching: true,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_SUCCESS:
      return {
        ...state,
        owner: {
          id: action.payload.data.id,
          data: action.payload.data.attributes || null,
          isFetching: false,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_FAILURE:
      return {
        ...state,
        owner: {
          ...state.owner,
          isFetching: false,
          error: true,
        },
      };
    case OwnerActions.FETCH_OWNER_RENTALS_PENDING:
      return {
        ...state,
        rentals: {
          ...state.rentals,
          data: [],
          isFetching: true,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_RENTALS_SUCCESS:
      return {
        ...state,
        rentals: {
          data: action.payload.data || [],
          meta: action.payload.meta || null,
          isFetching: false,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_RENTALS_FAILURE:
      return {
        ...state,
        rentals: {
          ...state.rentals,
          isFetching: false,
          error: true,
        },
      };
    case OwnerActions.FETCH_OWNER_REVIEWS_PENDING:
      return {
        ...state,
        reviews: {
          ...state.reviews,
          data: [],
          isFetching: true,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_REVIEWS_SUCCESS:
      return {
        ...state,
        reviews: {
          data: action.payload.reviews || [],
          totalReviews: action.payload.totalReviews,
          isFetching: false,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_REVIEWS_FAILURE:
      return {
        ...state,
        reviews: {
          ...state.reviews,
          isFetching: false,
          error: true,
        },
      };
    case OwnerActions.FETCH_OWNER_RESET:
      return {
        ...state,
        rentals: {
          data: [],
          meta: null,
          isFetching: false,
          error: false,
        },
        reviews: {
          data: [],
          totalReviews: 0,
          isFetching: false,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_LOCATIONS_PENDING:
      return {
        ...state,
        locations: {
          data: [],
          isFetching: true,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_LOCATIONS_SUCCESS:
      return {
        ...state,
        locations: {
          data: action.payload.data || [],
          fallback: action.payload.fallback,
          isFetching: false,
          error: false,
        },
      };
    case OwnerActions.FETCH_OWNER_LOCATIONS_FAILURE:
      return {
        ...state,
        locations: {
          ...state.locations,
          isFetching: false,
          error: true,
        },
      };
    default:
      return state;
  }
}
